'use client'

import { AddToCartModal } from '@/components/cart/add-to-cart-modal'
import { ContentImage } from '@/components/ui/content-image'
import { useTapLock } from '@/hooks/use-tap-lock'
import { useAppDispatch } from '@/lib/store'
import { addToCart, openCartDrawer } from '@/lib/store/features/cart/cartSlice'
import { IProductVariant } from '@/lib/types/core/product-variant'
import { ProductItem } from '@/lib/types/operations/product'
import { cn } from '@/lib/utils'
import { resolvePrice } from '@/lib/utils/price-resolution'
import { showToast, ToastSuppress } from '@/lib/utils/toast'
import Link from 'next/link'
import { useCallback, useMemo, useState } from 'react'

interface ProductCardWrapProps {
  product: ProductItem
  priceTier: 'RETAIL' | 'WHOLESALE'
  categoryName?: string
  onAddToCart?: (product: ProductItem) => void
}

type StockLevel = 'in' | 'low' | 'out'

// Solid fills (not the /15 tint used for in-page chips elsewhere): this
// badge floats over an arbitrary product photo, so it needs to stay legible
// regardless of what's underneath, not just against a plain surface.
const STOCK_STYLES: Record<StockLevel, { level: StockLevel; label: string; badgeClass: string; dotClass: string }> = {
  in: { level: 'in', label: 'IN STOCK', badgeClass: 'bg-success text-success-foreground', dotClass: 'bg-success-foreground' },
  low: { level: 'low', label: 'LOW STOCK', badgeClass: 'bg-warning text-warning-foreground', dotClass: 'bg-warning-foreground' },
  out: { level: 'out', label: 'OUT OF STOCK', badgeClass: 'bg-danger text-danger-foreground', dotClass: 'bg-danger-foreground' },
}

function getStockInfo(product: ProductItem): { level: StockLevel; label: string; badgeClass: string; dotClass: string } {
  let totalQuantity = 0

  if (product.inventoryItems && product.inventoryItems.length > 0) {
    totalQuantity = product.inventoryItems.reduce(
      (sum, item) => sum + (Number(item.quantity) || 0),
      0
    )
  }

  if (product.inventory?.quantity) {
    totalQuantity += Number(product.inventory.quantity) || 0
  }

  if (totalQuantity > 10) return STOCK_STYLES.in
  if (totalQuantity > 0) return STOCK_STYLES.low
  return STOCK_STYLES.out
}

// Card image fallback: product-level media, then an in-stock variant's
// swatch photo, then the first variant with any photo at all. Wrap-vinyl
// products (PWF, SW900, 2080) carry no product-level media, only per-variant
// swatch photography (see scripts/supplier-assets/wire-supplier-swatches.ts
// and wire-product-hero-images.ts), so without this chain every card on
// those catalogs falls straight to the placeholder.
function resolveCardImage(product: ProductItem): { url?: string; alt: string } {
  const alt = product.name || 'Product image'
  if (product.media?.[0]?.url) return { url: product.media[0].url, alt }

  const variants = (product.variants as Array<{ id: string; media?: Array<{ url: string }> }>) || []
  const variantsWithMedia = variants.filter((v) => v.media?.[0]?.url)
  if (variantsWithMedia.length === 0) return { url: undefined, alt }

  const inStockVariantIds = new Set(
    (product.inventoryItems || [])
      .filter((item) => item.variantId && Number(item.quantity) > 0)
      .map((item) => item.variantId)
  )
  const chosen = variantsWithMedia.find((v) => inStockVariantIds.has(v.id)) || variantsWithMedia[0]
  return { url: chosen.media?.[0]?.url, alt }
}

function formatCurrency(value: number): string {
  if (value >= 1000) {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  }
  return `$${value.toFixed(2)}`
}

export function ProductCardWrap({
  product,
  priceTier,
  categoryName,
  onAddToCart
}: ProductCardWrapProps) {
  const dispatch = useAppDispatch()
  const [modalOpen, setModalOpen] = useState(false)
  const stock = useMemo(() => getStockInfo(product), [product])
  const cardImage = useMemo(() => resolveCardImage(product), [product])
  const displayCategory = categoryName || ''
  // Synchronous lock: a rapid double-tap must not double the cart quantity
  const acquireTapLock = useTapLock()

  const hasVariants =
    Array.isArray(product.variants) && product.variants.length > 0

  // For fixed-price items (no variants with retailPrice), skip the modal
  const isOutOfStock = stock.level === 'out'

  const handleAddToCartClick = useCallback(() => {
    if (isOutOfStock) return
    if (!hasVariants) {
      // Second tap inside the lock window is a no-op, not a quantity increment
      if (!acquireTapLock()) return
      // Simple fixed-price product: dispatch directly
      dispatch(
        addToCart({
          productId: product.id || '',
          product,
          quantity: 1,
          purchaseMode: 'fixed'
        })
      )
      showToast({
        title: 'Added to cart',
        color: 'success',
        timeout: 3000,
        skipIf: ToastSuppress.onMobile
      })
      dispatch(openCartDrawer())
      return
    }
    // Has variants: open modal
    setModalOpen(true)
  }, [isOutOfStock, hasVariants, product, dispatch, acquireTapLock])

  // Resolve pricing from first variant
  const pricing = useMemo(() => {
    if (!hasVariants) return null

    const variant = product.variants![0]
    // Build a VariantPricing-compatible object. retailPrice/wholesalePrice are
    // the real DB columns; pricePerUnit (attributeValues-derived) is only a
    // fallback for variants seeded before those columns existed.
    const variantPricing = {
      retailPrice: variant.retailPrice ?? variant.pricePerUnit ?? null,
      wholesalePrice: variant.wholesalePrice ?? null,
      rollPrice: (variant.rollPrice as number) ?? null,
      rollLengthUnits: (variant.rollLengthUnits as number) ?? null
    }

    const perUnit = resolvePrice(variantPricing, priceTier)
    const unit = String((variant as any).unitOfMeasure || '').toUpperCase()
    return {
      perUnit,
      perUnitLabel: unit === 'YARD' ? 'Per Yd' : unit === 'METER' ? 'Per M' : 'Per Ft',
      rollPrice: variantPricing.rollPrice,
      width: variant.widthInches || product.width
    }
  }, [hasVariants, product, priceTier])

  const showSpecsBar = pricing && pricing.perUnit !== null
  const productSlug = product.slug || product.id

  return (
    <div data-testid="product-card" className="group bg-surface rounded-2xl border border-border overflow-hidden transition-colors duration-200 hover:border-foreground/20">
      {/* Image area. Badges are floating rounded chips inset from the edges
          (matches the service card treatment) instead of a full-width bar.
          Stock sits top-right; category sits bottom-left, each its own
          absolute corner now that they no longer share one flex row - the
          category badge still truncates via its own max-width so a long
          category name can't run past the card edge. */}
      <Link href={`/products/${productSlug}`} className="relative block aspect-[4/3] overflow-hidden bg-[var(--surface-secondary)]">
        <ContentImage
          src={cardImage.url}
          alt={cardImage.alt}
          contentType="PRODUCT"
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          fallbackSrc="/images/placeholder-product.jpg"
        />

        {/* The three labels here ("IN STOCK" / "LOW STOCK" / "OUT OF
            STOCK") are matched verbatim by tests/e2e/helpers/checkout-flow.ts
            to skip out-of-stock cards; keep the text and stay compact via
            size/padding only, never by shortening the words. */}
        <span
          className={cn(
            'absolute top-2 right-2 z-10 inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase whitespace-nowrap backdrop-blur-sm',
            stock.badgeClass
          )}
        >
          <span className={cn('w-1.5 h-1.5 rounded-full', stock.dotClass)} />
          {stock.label}
        </span>

        {displayCategory && (
          <span className="absolute bottom-2 left-2 z-10 max-w-[calc(100%-1rem)] min-w-0 truncate rounded-full bg-black/50 px-2 py-0.5 text-[9px] font-semibold uppercase text-white backdrop-blur-sm">
            {displayCategory}
          </span>
        )}
      </Link>

      {/* Body */}
      <div className="p-4 flex flex-col gap-2">
        {/* Product name */}
        <Link href={`/products/${productSlug}`}>
          <h3
            title={product.name}
            className="font-bold text-sm text-foreground line-clamp-2 hover:underline transition-colors cursor-pointer"
          >
            {product.name}
          </h3>
        </Link>

        {/* Specs bar or simple price */}
        {showSpecsBar ? (
          <div className="mt-1 bg-background rounded-xl flex divide-x divide-border text-center">
            {/* Width */}
            <div className="flex-1 py-2 px-1">
              <div className="text-[10px] text-muted uppercase tracking-wide">
                Width
              </div>
              <div className="text-sm font-bold text-muted">
                {pricing.width ? `${pricing.width}"` : '–'}
              </div>
            </div>
            {/* Per-unit price, labeled by the variant's unit of measure */}
            <div className="flex-1 py-2 px-1">
              <div className="text-[10px] text-muted uppercase tracking-wide">
                {pricing.perUnitLabel}
              </div>
              <div className="text-sm font-bold text-foreground">
                {pricing.perUnit !== null
                  ? `$${pricing.perUnit.toFixed(2)}`
                  : '–'}
              </div>
            </div>
            {/* Roll */}
            <div className="flex-1 py-2 px-1">
              <div className="text-[10px] text-muted uppercase tracking-wide">
                Roll
              </div>
              <div className="text-sm font-bold text-success">
                {pricing.rollPrice
                  ? formatCurrency(pricing.rollPrice)
                  : '–'}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-1 bg-background rounded-xl py-2 text-center">
            <div className="text-sm font-bold text-foreground">
              {product.price ? `$${product.price.toFixed(2)}` : 'Contact for pricing'}
            </div>
          </div>
        )}

        {/* Add to Cart is the sole button: the image and name already link
            to the product page, so a second "Details" pill only duplicated
            that path and was the reason two buttons wrapped to two lines on
            narrow cards. */}
        <button
          type="button"
          onClick={handleAddToCartClick}
          disabled={isOutOfStock}
          aria-disabled={isOutOfStock}
          className={cn(
            'mt-1 w-full px-3 py-2.5 rounded-full text-xs font-semibold transition-colors',
            isOutOfStock
              ? 'bg-surface-tertiary text-muted cursor-not-allowed'
              : 'text-accent-foreground bg-accent hover:bg-[var(--btn-primary-bg-hover)]'
          )}
        >
          {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
        </button>
      </div>

      <AddToCartModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        product={product}
        variant={(product.variants?.[0] ?? null) as IProductVariant | null}
        priceTier={priceTier}
      />
    </div>
  )
}
